import React, {useEffect, useState} from "react";
import {Button, Card, CardBody} from "reactstrap";
import CardHeader from "components/CardHeader";
import {
    disableTenantWebPush,
    enableTenantWebPush,
    tenantPushEnabledLocally,
    webPushSupported
} from "services/WebPush";
import {useOfficeTranslation} from "i18n/OfficeTranslation";

interface Props {
    tenantId: string;
}

const TenantPushNotifications: React.FC<Props> = ({ tenantId }) => {
    const {t} = useOfficeTranslation();
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const supported = webPushSupported();

    useEffect(() => {
        setEnabled(tenantPushEnabledLocally(tenantId));
    }, [tenantId]);

    const togglePush = async () => {
        setBusy(true);
        setMessage("");
        try {
            if (enabled) {
                await disableTenantWebPush(tenantId);
                setEnabled(false);
                setMessage(t("tenants.tenantPushNotifications.disabledForThisTenant"));
            } else {
                await enableTenantWebPush(tenantId);
                setEnabled(true);
                setMessage(t("tenants.tenantPushNotifications.enabledForThisTenant"));
            }
        } catch (error: any) {
            setMessage(error?.message || t("tenants.tenantPushNotifications.pushNotificationsCouldNotBeChanged"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <CardBody>
                <CardHeader title={t("tenants.tenantPushNotifications.title")} icon="mdi-bell-outline" />
                <div className="d-flex align-items-center justify-content-between gap-3 py-3 border-bottom">
                    <div>
                        <div className="fw-medium">
                            {t(enabled ? "common.status.enabled" : "common.status.disabled")}
                        </div>
                        <small className="text-muted">
                            {t(supported ? "pushNotifications.deviceScope" : "pushNotifications.unsupportedDevice")}
                        </small>
                    </div>
                    <Button
                        color={enabled ? "outline-secondary" : "primary"}
                        disabled={busy || !supported}
                        onClick={togglePush}
                        size="sm"
                    >
                        {t(busy ? "common.actions.working" : enabled ? "common.actions.disable" : "common.actions.enable")}
                    </Button>
                </div>
                {message && <p className="text-muted mt-3 mb-0">{message}</p>}
            </CardBody>
        </Card>
    );
};

export default TenantPushNotifications;
